// dashboard/useModalA11y.js
// Shared behavior for the app's centered-overlay modals (FinalizePatientModal,
// AssignModal, TrackerPopout) — none of them had any of this before the
// 2026-08-16 audit: no Escape-to-close, no focus moving into the dialog on
// open, no focus returning to whatever triggered it on close. All three are
// always-mounted-means-open (the parent conditionally renders them, there's
// no internal open/closed boolean), so this only needs to run once per
// mount, not react to an `active` flag.
//
// 2026-08-27 (client-quality audit) — added Tab/Shift-Tab containment and
// background inerting. Neither modal is rendered via a portal (confirmed:
// no createPortal anywhere in this codebase) — all three sit inline in the
// normal React tree, nested under App's own return. That rules out the
// usual "aria-hide document.body's other children" technique (it'd be a
// no-op here: #root is body's only real child and it contains the modal
// too, so there'd be no sibling left to hide). Instead this walks UP from
// the modal's own container to document.body and, at every level, hides
// only the siblings NOT on the path to the modal — correct regardless of
// how deeply nested the modal is, without needing a portal.

import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Dialogs can be nested (for example FinalizePatientModal opens from the
// patient drawer). Every instance listens at document level, so without an
// explicit stack one Escape press reaches both listeners and closes both
// layers. Tokens are module-local to this browser tab and are removed on
// unmount. The deepest open dialog handles keyboard events; for separate
// sibling dialogs, the most recently mounted one wins.
const openDialogStack = [];

// Hides every DOM sibling along the ancestor chain from `el` up to
// document.body — i.e. everything NOT on the direct path to `el` — via
// aria-hidden + the native `inert` attribute (removes it from the
// accessibility tree and from tab order / hit-testing in one step).
// Returns a cleanup function that restores exactly what it hid.
function inertOutside(el) {
  const hidden = [];
  let node = el;
  while (node && node !== document.body) {
    const parent = node.parentElement;
    if (parent) {
      for (const sibling of parent.children) {
        if (sibling !== node && !sibling.hasAttribute('aria-hidden')) {
          sibling.setAttribute('aria-hidden', 'true');
          sibling.setAttribute('inert', '');
          hidden.push(sibling);
        }
      }
    }
    node = parent;
  }
  return () => {
    for (const el of hidden) {
      el.removeAttribute('aria-hidden');
      el.removeAttribute('inert');
    }
  };
}

export function useModalA11y(onClose) {
  const containerRef = useRef(null);
  const triggerElRef = useRef(null);

  useEffect(() => {
    const dialogEntry = { ref: containerRef };
    openDialogStack.push(dialogEntry);
    triggerElRef.current = document.activeElement;
    containerRef.current?.focus();
    const restoreInert = containerRef.current ? inertOutside(containerRef.current) : () => {};

    const handleKeyDown = e => {
      const leaves = openDialogStack.filter(candidate => {
        const candidateEl = candidate.ref.current;
        if (!candidateEl) return false;
        return !openDialogStack.some(other =>
          other !== candidate && other.ref.current && candidateEl.contains(other.ref.current)
        );
      });
      if (leaves[leaves.length - 1] !== dialogEntry) return;
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !containerRef.current) return;
      const focusable = Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(elem => elem.offsetParent !== null); // skip hidden elements
      if (focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !containerRef.current.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const stackIndex = openDialogStack.lastIndexOf(dialogEntry);
      if (stackIndex !== -1) openDialogStack.splice(stackIndex, 1);
      restoreInert();
      // Return focus to whatever opened this, if it's still on the page —
      // it might not be, e.g. if the same action that closed the modal
      // also removed the row that triggered it.
      if (triggerElRef.current && document.contains(triggerElRef.current)) {
        triggerElRef.current.focus();
      }
    };
    // onClose is passed fresh from the caller's render most of the time
    // (an inline arrow function) — deliberately not in the dep array, or
    // this would tear down and re-attach the listener (and re-run the
    // focus-capture logic) on every parent render, not just on open/close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return containerRef;
}
